import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import * as ErrorActions from '../store/error/actions/error.actions';

/**
 * Global Error Handler
 *
 * Catches all uncaught errors in the application and dispatches them
 * to the NgRx error store for centralized error handling and display.
 *
 * This handles errors that are not caught by HTTP interceptors or
 * component-level error handling.
 *
 * Note: Uses lazy injection via Injector to avoid circular dependency issues
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private injector = inject(Injector);

  handleError(error: Error): void {
    // Log to console for debugging
    console.error('Global error caught:', error);

    // Cross-origin scripts (browser extensions, content blockers, third-party
    // resources) surface as the opaque "Script error." string with no file, line, or
    // stack — the browser strips detail for security. It carries no actionable info and
    // is not an app failure, so log it but do not toast the user. (Seen e.g. when the
    // iOS share sheet opens over the page.)
    if (GlobalErrorHandler.isCrossOriginScriptError(error)) {
      return;
    }

    // Lazy-inject Store to avoid circular dependency
    try {
      const store = this.injector.get(Store);
      store.dispatch(
        ErrorActions.GlobalError({
          error: {
            statusCode: 500,
            message: error.message || 'An unexpected error occurred',
            timestamp: new Date(),
            actionType: 'Uncaught Error',
            details: error,
          },
        }),
      );
    } catch (injectionError) {
      // If store isn't available yet, just log to console
      console.error('Could not dispatch error to store:', injectionError);
    }
  }

  /**
   * Opaque cross-origin script errors carry no actionable detail. They reach us in two
   * shapes:
   *  - the bare browser string "Script error." (sometimes without the trailing period);
   *  - Angular's provideBrowserGlobalErrorListeners() wrapper, whose message is
   *    "An ErrorEvent with no error occurred. See Error.cause for details: ..." with the
   *    original "Script error." tucked into error.cause.
   * Inspect both the message and the cause so either shape is caught.
   */
  private static isCrossOriginScriptError(error: Error): boolean {
    const cause = (error as Error & { cause?: unknown })?.cause;
    const causeText = typeof cause === 'string' ? cause : ((cause as Error)?.message ?? '');
    const text = `${error?.message ?? ''} ${causeText}`.toLowerCase();
    return text.includes('script error') || text.includes('errorevent with no error');
  }
}
